import {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

/**
 * Canonical file-queue types, lifted out of ConvertPage.tsx so they can be
 * shared with this context without a circular import.
 */
export type FileStatus =
  | "uploading"
  | "queued"
  | "converting"
  | "completed"
  | "failed"
  | "cancelled";

export interface ConversionFile {
  id: string;
  file: File | null;
  originalName: string;
  displayName: string;
  size: number;
  type: string;
  sourceFormat?: string; // Source file format extension
  outputFormat: string;
  status: FileStatus;
  progress: number;
  options: Record<string, any>;
  downloadUrl?: string;
  error?: string;
  sourceUrl?: string; // For URL-based files
  cloudSource?: boolean; // For cloud storage files
  // The server-side conversionJob id (distinct from this file's client-side
  // `id`), needed to call POST /api/files/conversions/:id/cancel so a
  // cancelled job is marked "cancelled" in the DB immediately instead of
  // sitting in "processing"/"queued" until it goes stale.
  conversionId?: string;
}

interface ConversionQueueContextValue {
  files: ConversionFile[];
  setFiles: Dispatch<SetStateAction<ConversionFile[]>>;
}

const ConversionQueueContext =
  createContext<ConversionQueueContextValue | null>(null);

/**
 * Holds the in-progress conversion queue above the router (see App.tsx),
 * instead of as local state inside ConvertPage. The Hierarchical Smart
 * Router redirects users between routes that render entirely different
 * <Route> entries (e.g. /convert -> /pdf-converter -> /pdf-to-docx-converter)
 * — each of those is a distinct element in React Router's route table, so
 * navigating between them unmounts and remounts the page component. Local
 * `useState` in ConvertPage would lose the 20+ queued files (and their
 * upload/conversion progress) on every one of those jumps. Lifting the
 * queue into a context that lives above <Routes> means it survives the
 * remount untouched.
 */
export function ConversionQueueProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [files, setFiles] = useState<ConversionFile[]>([]);
  return (
    <ConversionQueueContext.Provider value={{ files, setFiles }}>
      {children}
    </ConversionQueueContext.Provider>
  );
}

export function useConversionQueue(): ConversionQueueContextValue {
  const ctx = useContext(ConversionQueueContext);
  if (!ctx) {
    throw new Error(
      "useConversionQueue must be used within a ConversionQueueProvider",
    );
  }
  return ctx;
}
